import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  booleanAttribute,
  numberAttribute,
} from '@angular/core';
import { DsDotBulletComponent } from '../navigation/ds-dot-bullet.component';

@Component({
  selector: 'ds-carousel-b',
  standalone: true,
  imports: [DsDotBulletComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section
      class="ds-carousel-b"
      [class.ds-carousel-b--invert]="invert"
      aria-roledescription="carousel"
      [attr.aria-label]="ariaLabel"
    >
      <div
        #viewport
        class="ds-carousel-b__viewport"
        [class.ds-carousel-b__viewport--draggable]="resolvedItemCount > 1"
        [class.ds-carousel-b__viewport--dragging]="dragging"
        (pointerdown)="onViewportPointerDown($event)"
        (pointermove)="onViewportPointerMove($event)"
        (pointerup)="onViewportPointerUp($event)"
        (pointercancel)="onViewportPointerCancel($event)"
        (lostpointercapture)="onViewportLostPointerCapture($event)"
        (dragstart)="onViewportDragStart($event)"
      >
        <div
          #slot
          class="ds-carousel-b__slot"
          aria-live="polite"
          [style.padding]="slotPadding"
        >
          <ng-content />
        </div>
      </div>

      @if (resolvedItemCount > 1) {
        <div
          class="ds-carousel-b__indicators"
          role="group"
          [attr.aria-label]="indicatorsAriaLabel"
        >
          @for (index of indicatorIndexes; track index) {
            <ds-dot-bullet
              [state]="index === activeIndex
                ? (progressIndicatorEligible ? 'in-progress' : 'selected')
                : 'default'"
              [style]="'outlined'"
              [invert]="invert"
              [progressDurationSeconds]="indicatorProgressDurationSeconds"
              [progressStartPercent]="indicatorProgressStartPercent"
              [progressPaused]="index === activeIndex && progressIndicatorPaused"
              [ariaCurrent]="index === activeIndex"
              [ariaLabel]="indicatorLabel(index)"
              [tabIndex]="index === activeIndex ? 0 : -1"
              [disabled]="initialHintRunning"
              (clicked)="selectIndex(index)"
              (keydown)="onIndicatorKeydown($event, index)"
            />
          }
        </div>
      }
    </section>
  `,
  styles: [`
    :host {
      display: block;
      inline-size: 100%;
      max-inline-size: 100%;
    }

    .ds-carousel-b {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: var(--ds-carousel-b-content-gap);
      inline-size: 100%;
      max-inline-size: 100%;
      box-sizing: border-box;
    }

    .ds-carousel-b__viewport {
      position: relative;
      overflow: visible;
      inline-size: 100%;
      min-inline-size: 0;
    }

    .ds-carousel-b__viewport--draggable {
      cursor: grab;
      touch-action: pan-y pinch-zoom;
    }

    .ds-carousel-b__viewport--dragging {
      cursor: grabbing;
      user-select: none;
      -webkit-user-select: none;
    }

    .ds-carousel-b__viewport--animating {
      overflow: hidden;
      overflow: clip;
      overflow-clip-margin: var(--ds-carousel-b-slot-visual-overflow);
    }

    .ds-carousel-b__slot {
      position: relative;
      z-index: 1;
      inline-size: 100%;
      min-inline-size: 0;
      box-sizing: border-box;
      will-change: transform;
    }

    .ds-carousel-b__slot--outgoing {
      position: absolute;
      z-index: 0;
      inset: 0;
      pointer-events: none;
    }

    .ds-carousel-b__indicators {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--ds-carousel-b-indicators-gap);
      inline-size: 100%;
      min-block-size: var(--ds-dot-bullet-size);
    }

    @media (prefers-reduced-motion: reduce) {
      .ds-carousel-b__slot {
        will-change: auto;
      }
    }
  `],
})
export class DsCarouselBComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('viewport', { static: true }) private viewportRef?: ElementRef<HTMLElement>;
  @ViewChild('slot', { static: true }) private slotRef?: ElementRef<HTMLElement>;

  @Input({ transform: numberAttribute }) itemCount = 3;
  @Input({ transform: numberAttribute })
  set selectedIndex(value: number) {
    this.updateSelectedIndex(value);
  }

  get selectedIndex(): number {
    return this.selectedIndexValue;
  }

  @Input({ transform: booleanAttribute }) invert = false;
  @Input() slotPadding = 'var(--ds-carousel-b-slot-padding)';
  @Input({ transform: booleanAttribute }) autoPlay = false;
  @Input() slideDurationSeconds: number | string =
    'var(--ds-carousel-b-auto-play-slide-duration-seconds)';
  @Input({ transform: booleanAttribute }) showInitialHint = false;
  @Input() ariaLabel = 'Carrusel';
  @Input() indicatorsAriaLabel = 'Navegación del carrusel';
  @Input() indicatorAriaLabel = 'Ir a la diapositiva';

  @Output() selectedIndexChange = new EventEmitter<number>();

  private selectedIndexValue = 0;
  private viewReady = false;
  private animationFrameId: number | null = null;
  private animationGeneration = 0;
  private slideAnimations: Animation[] = [];
  private outgoingSlot: HTMLElement | null = null;
  private autoPlayTimerId: number | null = null;
  private autoPlayFullDurationMilliseconds = 0;
  private autoPlayElapsedMilliseconds = 0;
  private autoPlaySegmentStartTimestamp: number | null = null;
  private initialHintPlayed = false;
  initialHintRunning = false;
  private initialHintFrameId: number | null = null;
  private initialHintAnimations: Animation[] = [];
  private initialHintSnapshot: HTMLElement | null = null;
  private initialHintOriginalIndex = 0;
  private initialHintPreviewApplied = false;
  private initialHintIsVisible = false;
  private initialHintObserver: IntersectionObserver | null = null;
  private initialHintVisibilityFallbackCleanup: (() => void) | null = null;
  dragging = false;
  private activePointerId: number | null = null;
  private pointerStartX = 0;
  private pointerStartY = 0;
  private dragOffset = 0;
  private suppressNextViewportClick = false;
  private clickSuppressionTimerId: number | null = null;
  private readonly viewportClickCaptureHandler = (event: Event): void => {
    if (!this.suppressNextViewportClick) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.clearClickSuppression();
  };

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.viewportRef?.nativeElement.addEventListener(
      'click',
      this.viewportClickCaptureHandler,
      true,
    );
    this.observeInitialHintVisibility();
    this.restartAutoPlay();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.viewReady
      && changes['itemCount']
      && this.initialHintRunning
      && this.resolvedItemCount <= 1
    ) {
      this.cancelInitialHint(true);
    }

    if (
      this.viewReady
      && this.showInitialHint
      && (changes['showInitialHint'] || changes['itemCount'])
    ) {
      this.scheduleInitialHint();
    }

    if (changes['showInitialHint'] && !this.showInitialHint && this.initialHintRunning) {
      this.cancelInitialHint(true);
    }

    if (changes['itemCount'] || changes['autoPlay'] || changes['slideDurationSeconds']) {
      this.restartAutoPlay();
    }
  }

  ngOnDestroy(): void {
    this.viewportRef?.nativeElement.removeEventListener(
      'click',
      this.viewportClickCaptureHandler,
      true,
    );
    this.clearClickSuppression();
    this.resetPointerTracking();
    this.resetDraggedSlot();
    this.stopObservingInitialHintVisibility();
    this.clearAutoPlayTimer();
    this.cancelInitialHint(false);
    this.cancelSlideTransition();
  }

  get resolvedItemCount(): number {
    return Number.isFinite(this.itemCount) ? Math.max(0, Math.floor(this.itemCount)) : 0;
  }

  get activeIndex(): number {
    if (this.initialHintRunning) return this.initialHintOriginalIndex;
    return this.clampIndex(this.selectedIndex);
  }

  get indicatorIndexes(): number[] {
    return Array.from({ length: this.resolvedItemCount }, (_, index) => index);
  }

  get progressIndicatorEligible(): boolean {
    return this.autoPlay
      && this.resolvedItemCount > 1
      && !this.initialHintRunning;
  }

  get progressIndicatorPaused(): boolean {
    return this.activePointerId !== null;
  }

  get indicatorProgressDurationSeconds(): number {
    const remaining = this.autoPlayFullDurationMilliseconds - this.autoPlayElapsedMilliseconds;
    return Math.max(0, remaining) / 1000;
  }

  get indicatorProgressStartPercent(): number {
    if (this.autoPlayFullDurationMilliseconds <= 0) return 0;
    return Math.min(
      100,
      Math.max(0, (this.autoPlayElapsedMilliseconds / this.autoPlayFullDurationMilliseconds) * 100),
    );
  }

  indicatorLabel(index: number): string {
    return `${this.indicatorAriaLabel} ${index + 1} de ${this.resolvedItemCount}`;
  }

  selectIndex(index: number, direction?: -1 | 1): void {
    if (this.resolvedItemCount === 0) return;
    const nextIndex = this.clampIndex(index);
    if (nextIndex === this.activeIndex) return;

    this.updateSelectedIndex(nextIndex, direction);
    this.selectedIndexChange.emit(nextIndex);
  }

  onIndicatorKeydown(event: KeyboardEvent, index: number): void {
    const lastIndex = this.resolvedItemCount - 1;
    const movement = (() => {
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          return { index: index === lastIndex ? 0 : index + 1, direction: 1 as const };
        case 'ArrowLeft':
        case 'ArrowUp':
          return { index: index === 0 ? lastIndex : index - 1, direction: -1 as const };
        case 'Home':
          return { index: 0, direction: -1 as const };
        case 'End':
          return { index: lastIndex, direction: 1 as const };
        default:
          return null;
      }
    })();

    if (movement === null || movement.index < 0) return;

    event.preventDefault();
    this.selectIndex(movement.index, movement.direction);
    const group = (event.currentTarget as HTMLElement | null)?.parentElement;
    window.queueMicrotask(() => {
      group?.querySelectorAll<HTMLButtonElement>('.ds-dot-bullet')[movement.index]?.focus();
    });
  }

  onViewportPointerDown(event: PointerEvent): void {
    if (
      this.resolvedItemCount <= 1
      || this.activePointerId !== null
      || !event.isPrimary
      || (event.pointerType === 'mouse' && event.button !== 0)
    ) return;

    if (this.initialHintRunning) this.cancelInitialHint(true);
    this.cancelSlideTransition();
    this.pauseAutoPlay();
    this.activePointerId = event.pointerId;
    this.pointerStartX = event.clientX;
    this.pointerStartY = event.clientY;
    this.dragOffset = 0;

    try {
      this.viewportRef?.nativeElement.setPointerCapture(event.pointerId);
    } catch {
      // Synthetic pointer events and older browsers may not support pointer capture.
    }
  }

  onViewportPointerMove(event: PointerEvent): void {
    if (event.pointerId !== this.activePointerId) return;

    const deltaX = event.clientX - this.pointerStartX;
    const deltaY = event.clientY - this.pointerStartY;
    if (!this.dragging) {
      const activationDistance = this.resolveDragActivationDistance();
      if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < activationDistance) return;
      if (Math.abs(deltaY) >= Math.abs(deltaX)) {
        this.resetPointerTracking();
        this.resumeAutoPlay();
        return;
      }

      this.dragging = true;
      this.suppressNextViewportClick = true;
      this.viewportRef?.nativeElement.classList.add(
        'ds-carousel-b__viewport--animating',
        'ds-carousel-b__viewport--dragging',
      );
    }

    event.preventDefault();
    const viewport = this.viewportRef?.nativeElement;
    const slot = this.slotRef?.nativeElement;
    if (!viewport || !slot) return;

    const width = viewport.getBoundingClientRect().width;
    this.dragOffset = Math.min(Math.max(deltaX, -width), width);
    slot.style.transform = `translate3d(${this.dragOffset}px, 0, 0)`;
  }

  onViewportPointerUp(event: PointerEvent): void {
    if (event.pointerId !== this.activePointerId) return;
    if (!this.dragging) {
      this.resetPointerTracking();
      this.resumeAutoPlay();
      return;
    }

    event.preventDefault();
    const offset = this.dragOffset;
    const viewport = this.viewportRef?.nativeElement;
    const width = viewport?.getBoundingClientRect().width ?? 0;
    const commit = width > 0
      && Math.abs(offset) >= width * this.resolveDragCommitThresholdScale();

    this.resetPointerTracking();
    this.deferClickSuppressionClear();
    if (commit) {
      this.completeDragTransition(offset < 0 ? 1 : -1, offset);
    } else {
      this.snapDraggedSlotBack(offset);
    }
  }

  onViewportPointerCancel(event: PointerEvent): void {
    if (event.pointerId !== this.activePointerId) return;
    const offset = this.dragOffset;
    this.resetPointerTracking();
    this.deferClickSuppressionClear();
    this.snapDraggedSlotBack(offset);
  }

  onViewportLostPointerCapture(event: PointerEvent): void {
    if (event.pointerId !== this.activePointerId) return;
    const offset = this.dragOffset;
    this.resetPointerTracking(false);
    this.deferClickSuppressionClear();
    this.snapDraggedSlotBack(offset);
  }

  onViewportDragStart(event: DragEvent): void {
    if (this.activePointerId !== null) event.preventDefault();
  }

  private completeDragTransition(direction: -1 | 1, offset: number): void {
    const viewport = this.viewportRef?.nativeElement;
    const slot = this.slotRef?.nativeElement;
    if (!viewport || !slot || typeof window === 'undefined') return;

    const outgoingSlot = slot.cloneNode(true) as HTMLElement;
    outgoingSlot.removeAttribute('aria-live');
    outgoingSlot.setAttribute('aria-hidden', 'true');
    outgoingSlot.setAttribute('inert', '');
    outgoingSlot.classList.add('ds-carousel-b__slot--outgoing');
    viewport.appendChild(outgoingSlot);
    this.outgoingSlot = outgoingSlot;

    this.resetDraggedSlot(false);
    const nextIndex = direction === 1
      ? (this.activeIndex + 1) % this.resolvedItemCount
      : (this.activeIndex - 1 + this.resolvedItemCount) % this.resolvedItemCount;
    this.selectedIndexValue = nextIndex;
    this.selectedIndexChange.emit(nextIndex);
    this.restartAutoPlay();

    if (
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      || typeof slot.animate !== 'function'
    ) {
      this.finishSlideTransition(this.animationGeneration);
      return;
    }

    const generation = this.animationGeneration;
    this.animationFrameId = window.requestAnimationFrame(() => {
      this.animationFrameId = null;
      if (generation !== this.animationGeneration) return;

      const viewportStyle = window.getComputedStyle(viewport);
      const duration = Number.parseFloat(
        viewportStyle.getPropertyValue('--ds-carousel-b-transition-duration'),
      );
      const visualOverflowValue = Number.parseFloat(
        viewportStyle.getPropertyValue('--ds-carousel-b-slot-visual-overflow'),
      );
      const visualOverflow = Number.isFinite(visualOverflowValue) ? visualOverflowValue : 0;
      const travel = viewport.getBoundingClientRect().width + visualOverflow;
      if (!Number.isFinite(duration) || duration <= 0 || travel <= 0) {
        this.finishSlideTransition(generation);
        return;
      }

      const remainingDistanceScale = Math.max(0, Math.min(1, 1 - Math.abs(offset) / travel));
      const timing: KeyframeAnimationOptions = {
        duration: duration * remainingDistanceScale,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
        fill: 'both',
      };
      const outgoingOffset = -direction * travel;
      const incomingOffset = offset + direction * travel;

      this.slideAnimations = [
        outgoingSlot.animate(
          [
            { transform: `translate3d(${offset}px, 0, 0)` },
            { transform: `translate3d(${outgoingOffset}px, 0, 0)` },
          ],
          timing,
        ),
        slot.animate(
          [
            { transform: `translate3d(${incomingOffset}px, 0, 0)` },
            { transform: 'translate3d(0, 0, 0)' },
          ],
          timing,
        ),
      ];

      void Promise.all(
        this.slideAnimations.map((animation) => animation.finished.catch(() => undefined)),
      ).then(() => this.finishSlideTransition(generation));
    });
  }

  private snapDraggedSlotBack(offset: number): void {
    const viewport = this.viewportRef?.nativeElement;
    const slot = this.slotRef?.nativeElement;
    if (!viewport || !slot || typeof window === 'undefined') return;

    this.resetDraggedSlot(false);
    this.resumeAutoPlay();
    if (
      offset === 0
      || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      || typeof slot.animate !== 'function'
    ) {
      this.finishSlideTransition(this.animationGeneration);
      return;
    }

    const viewportStyle = window.getComputedStyle(viewport);
    const duration = Number.parseFloat(
      viewportStyle.getPropertyValue('--ds-carousel-b-transition-duration'),
    );
    const width = viewport.getBoundingClientRect().width;
    if (!Number.isFinite(duration) || duration <= 0 || width <= 0) {
      this.finishSlideTransition(this.animationGeneration);
      return;
    }

    const generation = this.animationGeneration;
    const distanceScale = Math.max(0, Math.min(1, Math.abs(offset) / width));
    const animation = slot.animate(
      [
        { transform: `translate3d(${offset}px, 0, 0)` },
        { transform: 'translate3d(0, 0, 0)' },
      ],
      {
        duration: duration * distanceScale,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
        fill: 'both',
      },
    );
    this.slideAnimations = [animation];
    void animation.finished
      .catch(() => undefined)
      .then(() => this.finishSlideTransition(generation));
  }

  private resetPointerTracking(releaseCapture = true): void {
    const pointerId = this.activePointerId;
    this.activePointerId = null;
    this.pointerStartX = 0;
    this.pointerStartY = 0;
    this.dragOffset = 0;
    if (!releaseCapture || pointerId === null) return;

    const viewport = this.viewportRef?.nativeElement;
    try {
      if (viewport?.hasPointerCapture(pointerId)) viewport.releasePointerCapture(pointerId);
    } catch {
      // Pointer capture may already have been released by the browser.
    }
  }

  private resetDraggedSlot(removeAnimatingClass = true): void {
    this.dragging = false;
    const viewport = this.viewportRef?.nativeElement;
    viewport?.classList.remove('ds-carousel-b__viewport--dragging');
    if (removeAnimatingClass) viewport?.classList.remove('ds-carousel-b__viewport--animating');
    const slot = this.slotRef?.nativeElement;
    if (slot) slot.style.transform = '';
  }

  private resolveDragActivationDistance(): number {
    const viewport = this.viewportRef?.nativeElement;
    if (!viewport || typeof window === 'undefined') return 0;
    const value = Number.parseFloat(
      window.getComputedStyle(viewport).getPropertyValue(
        '--ds-carousel-b-drag-activation-distance',
      ),
    );
    return Number.isFinite(value) ? Math.max(0, value) : 0;
  }

  private resolveDragCommitThresholdScale(): number {
    const viewport = this.viewportRef?.nativeElement;
    if (!viewport || typeof window === 'undefined') return 1;
    const value = Number.parseFloat(
      window.getComputedStyle(viewport).getPropertyValue(
        '--ds-carousel-b-drag-commit-threshold-scale',
      ),
    );
    return Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 1;
  }

  private deferClickSuppressionClear(): void {
    if (!this.suppressNextViewportClick || typeof window === 'undefined') return;
    if (this.clickSuppressionTimerId !== null) {
      window.clearTimeout(this.clickSuppressionTimerId);
    }
    this.clickSuppressionTimerId = window.setTimeout(() => this.clearClickSuppression());
  }

  private clearClickSuppression(): void {
    if (this.clickSuppressionTimerId !== null && typeof window !== 'undefined') {
      window.clearTimeout(this.clickSuppressionTimerId);
    }
    this.clickSuppressionTimerId = null;
    this.suppressNextViewportClick = false;
  }

  private updateSelectedIndex(value: number, direction?: -1 | 1): void {
    const previousIndex = this.activeIndex;
    this.selectedIndexValue = value;
    const nextIndex = this.activeIndex;

    if (!this.viewReady) return;
    if (this.initialHintRunning) return;

    this.restartAutoPlay();
    if (previousIndex === nextIndex) return;

    this.prepareSlideTransition(direction ?? (nextIndex > previousIndex ? 1 : -1));
  }

  private restartAutoPlay(): void {
    this.clearAutoPlayTimer();
    this.autoPlayElapsedMilliseconds = 0;
    if (
      !this.viewReady
      || !this.autoPlay
      || this.resolvedItemCount <= 1
    ) return;

    this.autoPlayFullDurationMilliseconds = this.resolveSlideDurationMilliseconds();
    this.beginAutoPlaySegment();
  }

  private beginAutoPlaySegment(): void {
    if (typeof window === 'undefined') return;
    const remaining = this.autoPlayFullDurationMilliseconds - this.autoPlayElapsedMilliseconds;
    if (!Number.isFinite(remaining) || remaining <= 0) return;

    this.autoPlaySegmentStartTimestamp = window.performance.now();
    this.autoPlayTimerId = window.setTimeout(() => {
      this.autoPlayTimerId = null;
      this.autoPlaySegmentStartTimestamp = null;
      this.autoPlayElapsedMilliseconds = this.autoPlayFullDurationMilliseconds;
      if (this.initialHintRunning) {
        this.cancelInitialHint(true);
      }
      const nextIndex = (this.activeIndex + 1) % this.resolvedItemCount;
      this.selectIndex(nextIndex, 1);
    }, remaining);
  }

  private pauseAutoPlay(): void {
    if (this.autoPlayTimerId === null || typeof window === 'undefined') return;
    if (this.autoPlaySegmentStartTimestamp !== null) {
      this.autoPlayElapsedMilliseconds += window.performance.now() - this.autoPlaySegmentStartTimestamp;
    }
    window.clearTimeout(this.autoPlayTimerId);
    this.autoPlayTimerId = null;
    this.autoPlaySegmentStartTimestamp = null;
  }

  private resumeAutoPlay(): void {
    if (!this.viewReady || !this.autoPlay || this.resolvedItemCount <= 1) return;
    this.beginAutoPlaySegment();
  }

  private clearAutoPlayTimer(): void {
    if (this.autoPlayTimerId === null || typeof window === 'undefined') return;
    window.clearTimeout(this.autoPlayTimerId);
    this.autoPlayTimerId = null;
    this.autoPlaySegmentStartTimestamp = null;
  }

  private resolveSlideDurationMilliseconds(): number {
    const viewport = this.viewportRef?.nativeElement;
    if (!viewport) return 0;

    const configuredSeconds = typeof this.slideDurationSeconds === 'number'
      ? this.slideDurationSeconds
      : Number.parseFloat(this.slideDurationSeconds);
    const tokenSeconds = Number.parseFloat(
      window.getComputedStyle(viewport).getPropertyValue(
        '--ds-carousel-b-auto-play-slide-duration-seconds',
      ),
    );
    const resolvedSeconds = Number.isFinite(configuredSeconds) && configuredSeconds > 0
      ? configuredSeconds
      : tokenSeconds;

    return Number.isFinite(resolvedSeconds) && resolvedSeconds > 0
      ? resolvedSeconds * 1000
      : 0;
  }

  private scheduleInitialHint(): boolean {
    const slot = this.slotRef?.nativeElement;
    if (
      !this.viewReady
      || !this.showInitialHint
      || !this.initialHintIsVisible
      || this.initialHintPlayed
      || this.resolvedItemCount <= 1
      || !slot
      || typeof window === 'undefined'
    ) return false;

    this.initialHintPlayed = true;
    this.stopObservingInitialHintVisibility();
    if (
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      || typeof slot.animate !== 'function'
    ) return false;

    this.initialHintOriginalIndex = this.clampIndex(this.selectedIndexValue);
    this.initialHintRunning = true;
    this.cancelSlideTransition();
    this.initialHintFrameId = window.requestAnimationFrame(() => this.beginInitialHint());
    return true;
  }

  private observeInitialHintVisibility(): void {
    const viewport = this.viewportRef?.nativeElement;
    if (!viewport || typeof window === 'undefined') return;

    this.stopObservingInitialHintVisibility();
    if (typeof window.IntersectionObserver === 'function') {
      this.initialHintObserver = new window.IntersectionObserver((entries) => {
        const entry = entries.find(({ target }) => target === viewport);
        if (!entry) return;

        this.initialHintIsVisible = entry.isIntersecting && entry.intersectionRatio > 0;
        if (this.initialHintIsVisible) this.scheduleInitialHint();
      });
      this.initialHintObserver.observe(viewport);
      return;
    }

    const updateVisibility = () => {
      const bounds = viewport.getBoundingClientRect();
      this.initialHintIsVisible = bounds.bottom > 0
        && bounds.right > 0
        && bounds.top < window.innerHeight
        && bounds.left < window.innerWidth;
      if (this.initialHintIsVisible) this.scheduleInitialHint();
    };
    window.addEventListener('scroll', updateVisibility, { capture: true, passive: true });
    window.addEventListener('resize', updateVisibility, { passive: true });
    this.initialHintVisibilityFallbackCleanup = () => {
      window.removeEventListener('scroll', updateVisibility, { capture: true });
      window.removeEventListener('resize', updateVisibility);
    };
    updateVisibility();
  }

  private stopObservingInitialHintVisibility(): void {
    this.initialHintObserver?.disconnect();
    this.initialHintObserver = null;
    this.initialHintVisibilityFallbackCleanup?.();
    this.initialHintVisibilityFallbackCleanup = null;
  }

  private beginInitialHint(): void {
    this.initialHintFrameId = null;
    const viewport = this.viewportRef?.nativeElement;
    const slot = this.slotRef?.nativeElement;
    if (!this.initialHintRunning || !viewport || !slot) return;

    const snapshot = slot.cloneNode(true) as HTMLElement;
    snapshot.removeAttribute('aria-live');
    snapshot.setAttribute('aria-hidden', 'true');
    snapshot.setAttribute('inert', '');
    snapshot.classList.add('ds-carousel-b__slot--outgoing');
    viewport.appendChild(snapshot);
    this.initialHintSnapshot = snapshot;

    slot.setAttribute('aria-live', 'off');
    slot.setAttribute('aria-hidden', 'true');
    slot.setAttribute('inert', '');

    const nextIndex = (this.initialHintOriginalIndex + 1) % this.resolvedItemCount;
    this.initialHintPreviewApplied = true;
    this.selectedIndexValue = nextIndex;
    this.selectedIndexChange.emit(nextIndex);

    this.initialHintFrameId = window.requestAnimationFrame(() => this.playInitialHint());
  }

  private playInitialHint(): void {
    this.initialHintFrameId = null;
    const viewport = this.viewportRef?.nativeElement;
    const slot = this.slotRef?.nativeElement;
    const snapshot = this.initialHintSnapshot;
    if (!this.initialHintRunning || !viewport || !slot || !snapshot) return;

    const viewportStyle = window.getComputedStyle(viewport);
    const duration = Number.parseFloat(
      viewportStyle.getPropertyValue('--ds-carousel-b-initial-hint-transition-duration'),
    );
    const peekScale = Number.parseFloat(
      viewportStyle.getPropertyValue('--ds-carousel-b-initial-hint-peek-scale'),
    );
    const easingControlValues = [
      '--ds-carousel-b-initial-hint-easing-x1-scale',
      '--ds-carousel-b-initial-hint-easing-y1-scale',
      '--ds-carousel-b-initial-hint-easing-x2-scale',
      '--ds-carousel-b-initial-hint-easing-y2-scale',
    ].map(propertyName => Number.parseFloat(viewportStyle.getPropertyValue(propertyName)));
    const visualOverflowValue = Number.parseFloat(
      viewportStyle.getPropertyValue('--ds-carousel-b-slot-visual-overflow'),
    );
    if (
      !Number.isFinite(duration)
      || duration <= 0
      || !Number.isFinite(peekScale)
      || easingControlValues.some(value => !Number.isFinite(value))
    ) {
      this.finishInitialHint();
      return;
    }

    const clampedPeekScale = Math.min(Math.max(peekScale, 0), 1);
    const peekPercent = clampedPeekScale * 100;
    const remainingPercent = 100 - peekPercent;
    const visualOverflow = Number.isFinite(visualOverflowValue) ? visualOverflowValue : 0;
    const nextRestingOffset = `calc(100% + ${visualOverflow}px)`;
    const initialHintEasing = `cubic-bezier(${easingControlValues.join(', ')})`;
    const timing: KeyframeAnimationOptions = {
      duration,
      easing: 'linear',
      fill: 'both',
    };

    viewport.classList.add('ds-carousel-b__viewport--animating');
    this.initialHintAnimations = [
      snapshot.animate(
        [
          { transform: 'translate3d(0, 0, 0)', easing: initialHintEasing },
          {
            transform: `translate3d(-${peekPercent}%, 0, 0)`,
            offset: 0.5,
            easing: initialHintEasing,
          },
          { transform: 'translate3d(0, 0, 0)' },
        ],
        timing,
      ),
      slot.animate(
        [
          {
            transform: `translate3d(${nextRestingOffset}, 0, 0)`,
            easing: initialHintEasing,
          },
          {
            transform: `translate3d(${remainingPercent}%, 0, 0)`,
            offset: 0.5,
            easing: initialHintEasing,
          },
          { transform: `translate3d(${nextRestingOffset}, 0, 0)` },
        ],
        timing,
      ),
    ];

    void Promise.all(
      this.initialHintAnimations.map((animation) => animation.finished.catch(() => undefined)),
    ).then(() => this.finishInitialHint());
  }

  private finishInitialHint(): void {
    if (!this.initialHintRunning) return;
    const slot = this.slotRef?.nativeElement;
    if (slot) slot.style.visibility = 'hidden';

    this.initialHintAnimations.forEach((animation) => animation.cancel());
    this.initialHintAnimations = [];

    if (this.initialHintPreviewApplied) {
      this.selectedIndexValue = this.initialHintOriginalIndex;
      this.selectedIndexChange.emit(this.initialHintOriginalIndex);
      this.initialHintPreviewApplied = false;
    }

    this.initialHintFrameId = window.requestAnimationFrame(() => {
      this.initialHintFrameId = null;
      this.cleanupInitialHint();
    });
  }

  private cancelInitialHint(restoreSelection: boolean): void {
    if (this.initialHintFrameId !== null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(this.initialHintFrameId);
      this.initialHintFrameId = null;
    }
    this.initialHintAnimations.forEach((animation) => animation.cancel());
    this.initialHintAnimations = [];

    if (restoreSelection && this.initialHintPreviewApplied) {
      this.selectedIndexValue = this.initialHintOriginalIndex;
      this.selectedIndexChange.emit(this.initialHintOriginalIndex);
    }
    this.initialHintPreviewApplied = false;
    this.cleanupInitialHint();
  }

  private cleanupInitialHint(): void {
    const slot = this.slotRef?.nativeElement;
    this.initialHintSnapshot?.remove();
    this.initialHintSnapshot = null;
    if (slot) {
      slot.style.visibility = '';
      slot.setAttribute('aria-live', 'polite');
      slot.removeAttribute('aria-hidden');
      slot.removeAttribute('inert');
    }
    this.viewportRef?.nativeElement.classList.remove('ds-carousel-b__viewport--animating');
    this.initialHintRunning = false;
  }

  private prepareSlideTransition(direction: -1 | 1): void {
    const viewport = this.viewportRef?.nativeElement;
    const slot = this.slotRef?.nativeElement;
    if (!viewport || !slot || typeof window === 'undefined') return;

    this.cancelSlideTransition();

    if (
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      || typeof slot.animate !== 'function'
    ) {
      return;
    }

    const outgoingSlot = slot.cloneNode(true) as HTMLElement;
    outgoingSlot.removeAttribute('aria-live');
    outgoingSlot.setAttribute('aria-hidden', 'true');
    outgoingSlot.setAttribute('inert', '');
    outgoingSlot.classList.add('ds-carousel-b__slot--outgoing');
    viewport.classList.add('ds-carousel-b__viewport--animating');
    viewport.appendChild(outgoingSlot);
    this.outgoingSlot = outgoingSlot;

    const generation = this.animationGeneration;
    this.animationFrameId = window.requestAnimationFrame(() => {
      this.animationFrameId = null;
      if (generation !== this.animationGeneration) return;

      const viewportStyle = window.getComputedStyle(viewport);
      const duration = Number.parseFloat(
        viewportStyle.getPropertyValue('--ds-carousel-b-transition-duration'),
      );
      if (!Number.isFinite(duration) || duration <= 0) {
        this.finishSlideTransition(generation);
        return;
      }

      const visualOverflowValue = Number.parseFloat(
        viewportStyle.getPropertyValue('--ds-carousel-b-slot-visual-overflow'),
      );
      const visualOverflow = Number.isFinite(visualOverflowValue) ? visualOverflowValue : 0;
      const incomingOffset = `calc(${direction * 100}% + ${direction * visualOverflow}px)`;
      const outgoingOffset = `calc(${direction * -100}% + ${direction * -visualOverflow}px)`;
      const timing: KeyframeAnimationOptions = {
        duration,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
        fill: 'both',
      };

      this.slideAnimations = [
        outgoingSlot.animate(
          [
            { transform: 'translate3d(0, 0, 0)' },
            { transform: `translate3d(${outgoingOffset}, 0, 0)` },
          ],
          timing,
        ),
        slot.animate(
          [
            { transform: `translate3d(${incomingOffset}, 0, 0)` },
            { transform: 'translate3d(0, 0, 0)' },
          ],
          timing,
        ),
      ];

      void Promise.all(this.slideAnimations.map((animation) => animation.finished.catch(() => undefined)))
        .then(() => this.finishSlideTransition(generation));
    });
  }

  private finishSlideTransition(generation: number): void {
    if (generation !== this.animationGeneration) return;
    this.slideAnimations.forEach((animation) => animation.cancel());
    this.slideAnimations = [];
    this.outgoingSlot?.remove();
    this.outgoingSlot = null;
    this.viewportRef?.nativeElement.classList.remove('ds-carousel-b__viewport--animating');
  }

  private cancelSlideTransition(): void {
    this.animationGeneration += 1;
    if (this.animationFrameId !== null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.slideAnimations.forEach((animation) => animation.cancel());
    this.slideAnimations = [];
    this.outgoingSlot?.remove();
    this.outgoingSlot = null;
    this.viewportRef?.nativeElement.classList.remove('ds-carousel-b__viewport--animating');
  }

  private clampIndex(index: number): number {
    if (this.resolvedItemCount === 0 || !Number.isFinite(index)) return 0;
    return Math.min(Math.max(0, Math.floor(index)), this.resolvedItemCount - 1);
  }
}
